import { Component, Input, Output, EventEmitter, HostBinding } from '@angular/core';
import { Router } from '@angular/router';

/**
 * Formats button text: capitalizes major words in Title / Camel Case,
 * keeping minor words lowercase (e.g. "a", "an", "the", "of", "for").
 * Examples: "CANCEL" -> "Cancel", "SAVE EDITS" -> "Save Edits", "APPLY FILTER" -> "Apply Filter"
 */
export function formatButtonText(text?: string): string {
  if (!text) return '';
  const str = String(text).trim();
  if (!str) return '';

  const acronyms = new Set([
    'FY', 'ID', 'PAN', 'GST', 'GSTIN', 'URL', 'API', 'OTP', 'PO', 'PR',
    'CVV', 'SSN', 'HRMS', 'TDS', 'TCS', 'CIN', 'LLPIN', 'IEC', 'IFSC',
    'MICR', 'SWIFT', 'NEFT', 'RTGS', 'IMPS', 'UPI', 'SKU', 'HSN', 'SAC',
    'PDF', 'CSV', 'MSME', 'TAN', 'VAT', 'CST', 'SEZ', 'LUT', 'DSO', 'UTR'
  ]);

  const words = str.split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';

  return words
    .map((word) => {
      const upper = word.toUpperCase();
      if (acronyms.has(upper)) {
        return upper;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

@Component({
  selector: 'app-button',
  templateUrl: './app-button.component.html',
  styleUrls: ['./app-button.component.scss'],
})
export class AppButtonComponent {
  @Input() labelText: string = '';
  @Input() label: string = '';
  @Input() variant: 'primary' | 'secondary' | 'outline' | 'danger' = 'primary';
  @Input() size: 'large' | 'medium' | 'small' | 'compact' = 'large';
  @Input() loading: boolean = false;
  @Input() disabled: boolean = false;
  @Input() fullWidth: boolean = true;
  @Input() width: string = '';
  @Input() minWidth: string = '';
  @Input() maxWidth: string = '';
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() startIcon: string = '';
  @Input() endIcon: string = '';
  @Input() customClass: string = '';

  @Output() btnClick = new EventEmitter<MouseEvent>();

  @HostBinding('style.width') get hostWidth() {
    if (this.width) return this.width;
    return this.fullWidth ? '100%' : null;
  }
  @HostBinding('style.minWidth') get hostMinWidth() {
    return this.minWidth || null;
  }
  @HostBinding('style.maxWidth') get hostMaxWidth() {
    return this.maxWidth || null;
  }
  @HostBinding('style.display') get hostDisplay() {
    return this.fullWidth || this.width === '100%' ? 'block' : 'inline-block';
  }
  @HostBinding('class.w-100') get hostW100() {
    return this.fullWidth || this.width === '100%';
  }
  @HostBinding('style.pointerEvents') get hostPointerEvents() {
    return this.disabled ? 'none' : null;
  }

  get formattedText(): string {
    const raw = this.labelText || this.label;
    return formatButtonText(raw);
  }

  onClick(event: MouseEvent): void {
    if (this.disabled || this.loading) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.btnClick.emit(event);
  }
}
